/**
 * Flow editor — tap-along sync calibration wizard (osu!/Clone-Hero style).
 *
 * Plays a steady click track (count-in + measured beats) on the shared audio
 * clock and asks the user to tap SPACE on every click. It matches each tap to
 * the nearest click's HEARD time (click ctx time + output latency, the same
 * latency getHeardPosition already compensates) and suggests the median offset
 * as the manual sync fine-tune. The user can Apply, Retry, or Cancel.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFlowStore } from './flowStore';
import { scheduleTickAt, getFlowAudioContext } from './flowSounds';
import { getOutputLatency } from './flowAudioEngine';
import { color, font } from '../../components/ui/theme';

const PERIOD = 0.5;     // seconds between clicks (120 BPM)
const COUNT_IN = 4;     // unmeasured lead-in clicks
const MEASURED = 8;     // measured clicks
const LEAD = 0.5;       // schedule the first click this far ahead

export default function FlowCalibrationWizard({ onClose }: { onClose: () => void }) {
  const setSyncOffsetMs = useFlowStore(s => s.setSyncOffsetMs);
  const setPlaying = useFlowStore(s => s.setPlaying);

  const [phase, setPhase] = useState<'running' | 'done'>('running');
  const [tapCount, setTapCount] = useState(0);
  const [result, setResult] = useState<number | null>(null);

  const heardClicksRef = useRef<number[]>([]); // ctx time each click is HEARD
  const tapsRef = useRef<number[]>([]);        // ctx time of each tap
  const finishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finish = useCallback(() => {
    const taps = tapsRef.current;
    const clicks = heardClicksRef.current;
    const deltas: number[] = [];
    for (const tap of taps) {
      let best = Infinity;
      for (const c of clicks) {
        const d = tap - c;
        if (Math.abs(d) < Math.abs(best)) best = d;
      }
      // Keep taps that clearly belong to a click (within ~60% of a beat).
      if (isFinite(best) && Math.abs(best) < PERIOD * 0.6) deltas.push(best);
    }
    if (deltas.length >= 3) {
      deltas.sort((a, b) => a - b);
      const med = deltas[Math.floor(deltas.length / 2)];
      setResult(Math.round(med * 1000)); // → milliseconds
    } else {
      setResult(null); // not enough clean taps
    }
    setPhase('done');
  }, []);

  const start = useCallback(() => {
    setPhase('running');
    setTapCount(0);
    setResult(null);
    tapsRef.current = [];
    const ctx = getFlowAudioContext();
    if (ctx.state === 'suspended') ctx.resume();
    const outLat = getOutputLatency();
    const t0 = ctx.currentTime + LEAD;
    const heard: number[] = [];
    for (let i = 0; i < COUNT_IN + MEASURED; i++) {
      const t = t0 + i * PERIOD;
      scheduleTickAt(t, i % 4 === 0);
      if (i >= COUNT_IN) heard.push(t + outLat); // measured clicks, as HEARD
    }
    heardClicksRef.current = heard;
    if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
    const totalMs = (LEAD + (COUNT_IN + MEASURED) * PERIOD + 0.4) * 1000;
    finishTimerRef.current = setTimeout(finish, totalMs);
  }, [finish]);

  // Pause music + start the click track on open.
  useEffect(() => {
    setPlaying(false);
    start();
    return () => { if (finishTimerRef.current) clearTimeout(finishTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture SPACE taps while running.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      e.preventDefault();
      if (phase !== 'running') return;
      tapsRef.current.push(getFlowAudioContext().currentTime);
      setTapCount(tapsRef.current.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase]);

  const apply = () => {
    if (result != null) setSyncOffsetMs(result);
    onClose();
  };

  return (
    <div style={backdropStyle} onMouseDown={onClose}>
      <div style={cardStyle} onMouseDown={e => e.stopPropagation()}>
        <div style={titleStyle}>🥁 Sync Calibration</div>

        {phase === 'running' && (
          <>
            <p style={hintStyle}>
              Tap <b style={{ color: '#22D3EE' }}>SPACE</b> on every click — don't look at the screen,
              just listen and tap to the beat.
            </p>
            <div style={bigCountStyle}>{tapCount}</div>
            <div style={subStyle}>taps recorded</div>
          </>
        )}

        {phase === 'done' && (
          <>
            {result != null ? (
              <>
                <p style={hintStyle}>Measured offset:</p>
                <div style={{ ...bigCountStyle, color: '#34D399' }}>{result > 0 ? '+' : ''}{result} ms</div>
                <div style={subStyle}>residual delay beyond the auto output-latency compensation</div>
                <div style={btnRowStyle}>
                  <button style={applyStyle} onClick={apply}>Apply as fine-tune</button>
                  <button style={ghostStyle} onClick={start}>↻ Retry</button>
                </div>
              </>
            ) : (
              <>
                <p style={hintStyle}>Not enough clean taps — try again and tap on every click.</p>
                <div style={btnRowStyle}>
                  <button style={applyStyle} onClick={start}>↻ Retry</button>
                </div>
              </>
            )}
          </>
        )}

        <button style={closeStyle} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 60,
  background: 'rgba(5, 3, 15, 0.75)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
  fontFamily: font.sans,
};
const cardStyle: React.CSSProperties = {
  width: 340, padding: '22px 24px',
  background: 'rgba(12, 8, 28, 0.96)',
  border: '1.5px solid rgba(34, 211, 238, 0.4)',
  borderRadius: 14, textAlign: 'center', color: color.ink,
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
  display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center',
};
const titleStyle: React.CSSProperties = { fontSize: 14, fontWeight: 800, letterSpacing: 1, color: '#22D3EE' };
const hintStyle: React.CSSProperties = { fontSize: 12, color: '#C4BEE0', lineHeight: 1.5, margin: 0 };
const bigCountStyle: React.CSSProperties = { fontSize: 44, fontWeight: 800, color: color.ink, lineHeight: 1.1 };
const subStyle: React.CSSProperties = { fontSize: 10, color: '#8A82B0', letterSpacing: 0.4 };
const btnRowStyle: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 6, width: '100%' };
const applyStyle: React.CSSProperties = {
  flex: 1, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
  background: 'linear-gradient(135deg, rgba(34,211,238,0.3), rgba(124,58,237,0.25))',
  border: '1px solid rgba(34,211,238,0.5)', color: '#E8FBFF', fontWeight: 700, fontSize: 12,
  fontFamily: font.sans,
};
const ghostStyle: React.CSSProperties = {
  flex: 1, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
  background: 'rgba(30,20,50,0.6)', border: '1px solid rgba(120,80,220,0.3)',
  color: color.inkDim, fontWeight: 600, fontSize: 12, fontFamily: font.sans,
};
const closeStyle: React.CSSProperties = {
  marginTop: 4, background: 'none', border: 'none', color: color.inkFaint,
  fontSize: 11, cursor: 'pointer', fontFamily: font.sans,
};
